/**
 * Pop-up de alerta para a recepção — dois tipos de pedido do médico:
 *
 *  1. "Chamar paciente" (status CALLED): o médico/recepção chamou o
 *     paciente para uma sala. (comportamento original)
 *  2. "Dilatar pupila" (oftalmo): o médico enviou o paciente de volta pra
 *     recepção pra DILATAR a pupila — a entrada volta pra WAITING (sala de
 *     espera) e carimba `dilationRequestedAt`. A secretária precisa pingar
 *     o colírio e manter o paciente na sala de espera até ser rechamado.
 *
 * Componente global montado no Shell. Mantém um listener em tempo real da
 * fila de HOJE e abre um pop-up modal que SÓ some quando a recepção confirma
 * ("Ciente"/"Fechar") — garantindo que o pedido do médico não passe
 * despercebido, mesmo que ela esteja em outra tela do sistema.
 *
 * Regras:
 *  - Só aparece para papéis de recepção (mesma regra dos briefings:
 *    ADMIN / SECRETARIA / RECEPCIONISTA). Médicos não veem.
 *  - Nunca mostra o pedido feito pelo PRÓPRIO usuário (`calledBy` /
 *    `dilationRequestedBy`).
 *  - Respeita o escopo de agenda do perfil (Equipe → `allowedProfessionalIds`):
 *    recepção escopada só vê chamada/dilatação de paciente dos médicos dela.
 *    Vazio = todos.
 *  - E o local de atendimento: paciente que chegou em OUTRO lugar (outra
 *    recepção) não pipoca aqui — ver utils/serviceLocal.
 *  - Vários pedidos viram uma fila: "Ciente"/"Fechar" passa para o próximo.
 *  - Toca o mesmo ding da agenda para reforçar o alerta em qualquer tela.
 */
import { useEffect, useRef, useState } from 'react';
import { Megaphone, X, DoorOpen, Clock, User as UserIcon, Droplets } from 'lucide-react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import { useAuth } from '@/hooks/useAuth';
import { canSee } from '@/hooks/usePendencias';
import { isOtherLocal } from '@/utils/serviceLocal';
import { playCallChime } from '@/utils/chime';
import type { QueueEntry } from '@/types/models';

type AlertKind = 'CALL' | 'DILATION';

interface AlertItem {
  entry: QueueEntry;
  kind: AlertKind;
  /** Chave única por evento (tipo + entryId + timestamp) — dedupe na fila
   *  local e permite re-alertar quando o mesmo paciente é re-chamado /
   *  re-enviado pra dilatar (timestamp novo → chave nova). */
  key: string;
}

function fmtTime(ms: number | undefined): string {
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export const CallAlertWatcher = () => {
  const { clinicId, role, user, profile } = useAuth();
  const uid = user?.uid;
  // Chave estável pro effect (o array muda de identidade a cada render).
  const scopeKey = (profile?.allowedProfessionalIds ?? []).join(',');
  const myLocal = profile?.serviceLocal;
  // Fila de pedidos aguardando "ciência" da recepção (um de cada vez).
  const [pending, setPending] = useState<AlertItem[]>([]);
  // entryId → timestamp já visto, separado por tipo, pra só reagir a
  // eventos novos (ou re-disparos com timestamp maior).
  const seenCallRef = useRef<Map<string, number>>(new Map());
  const seenDilationRef = useRef<Map<string, number>>(new Map());
  const firstSnapshotRef = useRef(true);

  useEffect(() => {
    if (!clinicId || !canSee(role, 'BRIEFING_NEEDED')) return;
    const scope = scopeKey ? scopeKey.split(',') : [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const queueQuery = query(
      collection(db, 'clinics', clinicId, 'queue'),
      where('arrivedAt', '>=', start.getTime()),
    );
    // Reset por clínica/sessão — não herdar "vistos".
    seenCallRef.current = new Map();
    seenDilationRef.current = new Map();
    firstSnapshotRef.current = true;

    const unsub = onSnapshot(
      queueQuery,
      (snap) => {
        const fresh: AlertItem[] = [];
        snap.docs.forEach((d) => {
          const e = d.data() as QueueEntry;
          // Paciente de médico fora do escopo desta recepção não é com ela.
          if (scope.length > 0 && !scope.includes(e.professionalId)) return;
          if (isOtherLocal(myLocal, e.serviceLocal)) return;

          // (1) Chamada para sala (status CALLED) — comportamento original.
          if (e.status === 'CALLED') {
            const prev = seenCallRef.current.get(e.id);
            const calledAt = e.calledAt ?? 0;
            if (prev === undefined || calledAt > prev) {
              seenCallRef.current.set(e.id, calledAt);
              // Primeira carga não dispara pop-up (eventos já existentes ao
              // abrir o sistema). Nunca mostra o pedido do próprio usuário.
              if (!firstSnapshotRef.current && e.calledBy !== uid) {
                fresh.push({ entry: e, kind: 'CALL', key: `call-${e.id}-${calledAt}` });
              }
            }
          }

          // (2) Pedido de dilatação (independe do status — volta como WAITING).
          if (e.dilationRequestedAt) {
            const prev = seenDilationRef.current.get(e.id);
            const reqAt = e.dilationRequestedAt;
            if (prev === undefined || reqAt > prev) {
              seenDilationRef.current.set(e.id, reqAt);
              if (!firstSnapshotRef.current && e.dilationRequestedBy !== uid) {
                fresh.push({ entry: e, kind: 'DILATION', key: `dil-${e.id}-${reqAt}` });
              }
            }
          }
        });
        if (fresh.length > 0) {
          setPending((prevQ) => {
            const keys = new Set(prevQ.map((p) => p.key));
            const toAdd = fresh.filter((it) => !keys.has(it.key));
            return toAdd.length ? [...prevQ, ...toAdd] : prevQ;
          });
          playCallChime();
        }
        firstSnapshotRef.current = false;
      },
      (err) => console.error('[call-alert] queue snapshot falhou:', err),
    );
    return () => unsub();
  }, [clinicId, role, uid, scopeKey, myLocal]);

  if (pending.length === 0) return null;

  const current = pending[0];
  const entry = current.entry;
  const remaining = pending.length - 1;
  const dismiss = () => setPending((qq) => qq.slice(1));
  const isDilation = current.kind === 'DILATION';

  // Campos exibidos variam por tipo: dilatação usa o timestamp/sala do
  // pedido de dilatação; chamada usa os campos de chamada.
  const requestedAt = isDilation ? entry.dilationRequestedAt : entry.calledAt;
  const room = isDilation ? entry.dilationRoom : entry.room;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 animate-fade-in"
      role="alertdialog"
      aria-modal="true"
      aria-label={isDilation ? 'Dilatar pupila do paciente' : 'Paciente chamado'}
    >
      <div
        className={`bg-surface rounded-2xl shadow-2xl border-2 w-full max-w-md overflow-hidden ${
          isDilation ? 'border-amber/60' : 'border-primary/40'
        }`}
      >
        <div
          className={`px-5 py-3 flex items-center gap-2 ${
            isDilation ? 'bg-amber text-[#1A1208]' : 'bg-primary text-on-primary'
          }`}
        >
          {isDilation ? (
            <Droplets size={22} className="shrink-0" />
          ) : (
            <Megaphone size={22} className="shrink-0" />
          )}
          <span className="font-headline font-bold text-lg">
            {isDilation ? 'Dilatar pupila' : 'Chamar paciente'}
          </span>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-on-surface-variant font-bold">Paciente</p>
            <p className="text-2xl font-bold text-on-surface break-words">{entry.patientName || 'Paciente'}</p>
          </div>

          {isDilation && (
            <div className="rounded-xl bg-amber/10 border border-amber/30 p-3 text-sm text-on-surface">
              Aplicar colírio para <b>dilatar a pupila</b> e manter o paciente na{' '}
              <b>sala de espera</b>. O médico vai chamá-lo(a) de volta em seguida.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <DoorOpen size={18} className={`shrink-0 ${isDilation ? 'text-amber' : 'text-primary'}`} />
              <div className="min-w-0">
                <p className="text-[10px] uppercase text-on-surface-variant font-bold">
                  {isDilation ? 'Volta p/ sala' : 'Sala'}
                </p>
                <p className="text-on-surface font-medium truncate">{room || '—'}</p>
              </div>
            </div>
            {entry.professionalName && (
              <div className="flex items-center gap-2">
                <UserIcon size={18} className={`shrink-0 ${isDilation ? 'text-amber' : 'text-primary'}`} />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase text-on-surface-variant font-bold">Profissional</p>
                  <p className="text-on-surface font-medium truncate">{entry.professionalName}</p>
                </div>
              </div>
            )}
          </div>

          {requestedAt && (
            <p className="text-xs text-on-surface-variant flex items-center gap-1">
              <Clock size={12} /> {isDilation ? 'Solicitado' : 'Chamado'} às {fmtTime(requestedAt)}
            </p>
          )}

          {remaining > 0 && (
            <p className={`text-xs font-bold ${isDilation ? 'text-amber' : 'text-primary'}`}>
              + {remaining} outro{remaining > 1 ? 's' : ''} pedido{remaining > 1 ? 's' : ''} na fila
            </p>
          )}

          <button
            onClick={dismiss}
            className={`w-full py-3 rounded-xl font-bold text-base hover:brightness-110 transition-all flex items-center justify-center gap-2 ${
              isDilation ? 'bg-amber text-[#1A1208]' : 'bg-primary text-on-primary'
            }`}
          >
            <X size={18} /> {isDilation ? 'Ciente' : 'Fechar'}
          </button>
        </div>
      </div>
    </div>
  );
};
